import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Copy, Plus, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui-kit/select';
import { Textarea } from '@/components/ui-kit/textarea';
import { QUESTION_FORMATS } from '../../constants/review-constants';
import { Question, QuestionFormatType, Section } from '../../types/form-engine.types';
import { changeQuestionFormat } from '../../utils/question-factory';
import { OptionActionConfig } from '../option-action-config/option-action-config';

const MAX_RATING_LIMIT = 20;

export interface QuestionBuilderCardProps {
  question: Question;
  onChange: (question: Question) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMove: (direction: -1 | 1) => void;
  sections?: Section[];
  currentSectionId?: string;
  isAnnualPromotionForm?: boolean;
}

export const QuestionBuilderCard = ({
  question,
  onChange,
  onDelete,
  onDuplicate,
  onMove,
  sections = [],
  currentSectionId,
  isAnnualPromotionForm,
}: QuestionBuilderCardProps) => {
  const { t } = useTranslation();
  const patch = (changes: Partial<Question>) => onChange({ ...question, ...changes });
  const isChoice = question.FormatType === 'multiple-choice' || question.FormatType === 'checkboxes';
  const isRating = question.FormatType === 'rating' || question.FormatType === 'nps';
  const hasSections = sections.length > 0;
  const options = question.Options ?? [];
  const likertLabels = question.LikertLabels ?? [];
  const likertStatements = question.LikertStatements ?? [];

  const setLikertLabels = (labels: string[]) => patch({ LikertLabels: labels, MinRating: 1, MaxRating: labels.length });

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 pt-6">
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-start">
          <div className="min-w-0 flex-1 space-y-3">
            <Input
              placeholder={t('QUESTION')}
              value={question.Question}
              onChange={(e) => patch({ Question: e.target.value })}
            />
            <Textarea
              rows={2}
              placeholder={t('QUESTION_DESCRIPTION')}
              value={question.QuestionDescription}
              onChange={(e) => patch({ QuestionDescription: e.target.value })}
            />
          </div>
          <div className="flex shrink-0 justify-end gap-1">
            <Button type="button" variant="ghost" size="icon" onClick={() => onMove(-1)}>
              <ArrowUp className="size-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" onClick={() => onMove(1)}>
              <ArrowDown className="size-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" onClick={onDuplicate}>
              <Copy className="size-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" onClick={onDelete}>
              <Trash className="size-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="w-full sm:w-48">
            <Label className="sr-only">{t('TYPE')}</Label>
            <Select
              value={question.FormatType}
              onValueChange={(value: QuestionFormatType) => onChange(changeQuestionFormat(question, value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUESTION_FORMATS.map((format) => (
                  <SelectItem key={format.key} value={format.key}>
                    {t(format.title)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={question.IsRequired} onCheckedChange={(checked) => patch({ IsRequired: Boolean(checked) })} />
            {t('REQUIRED')}
          </label>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={question.HasComment}
              onCheckedChange={(checked) => patch({ HasComment: Boolean(checked), Comments: checked ? question.Comments : '' })}
            />
            {t('ADD_COMMENT')}
          </label>

          {isAnnualPromotionForm && question.FormatType === 'multiple-choice' && (
            <>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={Boolean(question.IsPromotionTierQuestion)}
                  onCheckedChange={(checked) =>
                    patch({
                      IsPromotionTierQuestion: Boolean(checked),
                      IsPromotionRecommendationQuestion: checked ? false : question.IsPromotionRecommendationQuestion,
                    })
                  }
                />
                {t('PROMOTION_TIER_QUESTION')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={Boolean(question.IsPromotionRecommendationQuestion)}
                  onCheckedChange={(checked) =>
                    patch({
                      IsPromotionRecommendationQuestion: Boolean(checked),
                      IsPromotionTierQuestion: checked ? false : question.IsPromotionTierQuestion,
                    })
                  }
                />
                {t('PROMOTION_RECOMMENDATION_QUESTION')}
              </label>
            </>
          )}
        </div>

        {isChoice && (
          <div className="space-y-2">
            {options.map((option, index) => (
              <div key={index} className="flex flex-wrap items-center gap-2">
                <Input
                  className="min-w-48 flex-1"
                  value={option.Label}
                  onChange={(e) =>
                    patch({
                      Options: options.map((item, i) =>
                        i === index ? { ...item, Label: e.target.value, Value: e.target.value } : item
                      ),
                    })
                  }
                />
                {hasSections && question.FormatType === 'multiple-choice' && (
                  <OptionActionConfig
                    option={option}
                    sections={sections}
                    currentSectionId={currentSectionId}
                    onChange={(next) => patch({ Options: options.map((item, i) => (i === index ? next : item)) })}
                  />
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-lg"
                  onClick={() => patch({ Options: options.filter((_, i) => i !== index) })}
                >
                  <Trash className="size-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                patch({
                  Options: [...options, { Label: '', Value: `opt_${options.length + 1}`, Action: 'continue' }],
                })
              }
            >
              <Plus className="size-4" />
              {t('ADD_OPTION')}
            </Button>
          </div>
        )}

        {isRating && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t('MIN_LABEL')}</Label>
              <Input value={question.MinRatingLabel ?? ''} onChange={(e) => patch({ MinRatingLabel: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('MAX_LABEL')}</Label>
              <Input value={question.MaxRatingLabel ?? ''} onChange={(e) => patch({ MaxRatingLabel: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('MAX_RATING')}</Label>
              <Input
                type="number"
                min={1}
                max={MAX_RATING_LIMIT}
                value={question.MaxRating}
                onChange={(e) => patch({ MaxRating: Math.min(e.target.valueAsNumber || 0, MAX_RATING_LIMIT) })}
              />
            </div>
          </div>
        )}

        {question.FormatType === 'likert-scale' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t('SCALE_LABELS')}</Label>
              {likertLabels.map((label, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={label}
                    onChange={(e) => setLikertLabels(likertLabels.map((l, i) => (i === index ? e.target.value : l)))}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-lg"
                    disabled={likertLabels.length <= 2}
                    onClick={() => setLikertLabels(likertLabels.filter((_, i) => i !== index))}
                  >
                    <Trash className="size-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLikertLabels([...likertLabels, `Label ${likertLabels.length + 1}`])}
              >
                <Plus className="size-4" />
                {t('ADD_SCALE_LABEL')}
              </Button>
            </div>

            <div className="space-y-2">
              <Label>{t('STATEMENTS')}</Label>
              {likertStatements.map((statement, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={statement.Statement}
                    onChange={(e) =>
                      patch({
                        LikertStatements: likertStatements.map((s, i) =>
                          i === index ? { ...s, Statement: e.target.value } : s
                        ),
                      })
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-lg"
                    onClick={() => patch({ LikertStatements: likertStatements.filter((_, i) => i !== index) })}
                  >
                    <Trash className="size-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  patch({
                    LikertStatements: [
                      ...likertStatements,
                      { Statement: `Statement ${likertStatements.length + 1}`, Value: [] },
                    ],
                  })
                }
              >
                <Plus className="size-4" />
                {t('ADD_STATEMENT')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
